/**
 * /admin/contact-messages — messages depuis la page Contact.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
    Loader2, ShieldAlert, Mail, Phone, Calendar, Trash2, MessageSquare, Reply,
} from "lucide-react";


export default function AdminContactMessages() {
    const { user, loading: authLoading } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/contact-messages");
            setItems(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.role === "admin") load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const onDelete = async () => {
        if (!selected) return;
        if (!confirm("Supprimer définitivement ce message ?")) return;
        setDeleting(true);
        try {
            await api.delete(`/admin/contact-messages/${selected.id}`);
            toast.success("Message supprimé");
            setSelected(null);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setDeleting(false);
        }
    };

    if (authLoading) return <div className="em-container py-32 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" /></div>;
    if (!user || user.role !== "admin") {
        return (
            <div className="em-container py-20">
                <ShieldAlert className="h-12 w-12 text-destructive" />
                <h1 className="font-display text-4xl font-black text-primary mt-4">Accès refusé</h1>
                <Button asChild className="rounded-full mt-6 bg-primary hover:bg-primary/90"><Link to="/">Retour</Link></Button>
            </div>
        );
    }

    return (
        <div className="em-container py-8" data-testid="admin-contact-page">
            <p className="em-eyebrow flex items-center gap-2"><MessageSquare className="h-3.5 w-3.5" />Administration</p>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-primary mt-2">Messages de contact</h1>
            <p className="text-muted-foreground mt-3 max-w-2xl">Messages envoyés depuis la page Contact publique.</p>

            <Card className="rounded-3xl border-border mt-8">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="py-20 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" /></div>
                    ) : items.length === 0 ? (
                        <div className="py-16 text-center text-muted-foreground">
                            <MessageSquare className="h-12 w-12 mx-auto text-primary/30" />
                            <p className="mt-3">Aucun message pour l'instant.</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-border">
                            {items.map((m) => (
                                <button
                                    key={m.id}
                                    type="button"
                                    onClick={() => setSelected(m)}
                                    className="w-full text-left p-5 hover:bg-muted/40 transition-colors flex items-start gap-4"
                                >
                                    <div className="h-12 w-12 rounded-full bg-secondary flex items-center justify-center shrink-0">
                                        <MessageSquare className="h-5 w-5 text-primary" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-baseline justify-between gap-3">
                                            <p className="font-display font-bold text-primary">{m.full_name}</p>
                                            <span className="text-xs text-muted-foreground shrink-0">
                                                {new Date(m.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                                            </span>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-3">
                                            <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{m.email}</span>
                                            {m.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{m.phone}</span>}
                                        </p>
                                        <p className="text-sm mt-2 line-clamp-2 text-foreground/80">{m.message}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* DETAIL */}
            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">Message</DialogTitle>
                    </DialogHeader>
                    {selected && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <DetailField icon={Mail}     label="Email"     value={selected.email} />
                                <DetailField icon={Phone}    label="Téléphone" value={selected.phone} />
                                <DetailField icon={Calendar} label="Reçu le"   value={new Date(selected.created_at).toLocaleString("fr-FR")} />
                            </div>
                            <div className="p-4 bg-secondary rounded-2xl">
                                <p className="em-eyebrow mb-2">Message</p>
                                <p className="text-sm whitespace-pre-line">{selected.message}</p>
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setSelected(null)} className="rounded-full">Fermer</Button>
                        {selected?.email && (
                            <Button asChild className="rounded-full bg-primary hover:bg-primary/90">
                                <a href={`mailto:${selected.email}?subject=Re: votre message Energy Market`}>
                                    <Reply className="h-4 w-4 mr-2" />
                                    Répondre par email
                                </a>
                            </Button>
                        )}
                        <Button onClick={onDelete} disabled={deleting} variant="destructive" className="rounded-full">
                            {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                            Supprimer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


function DetailField({ icon: Icon, label, value }) {
    return (
        <div>
            <p className="em-eyebrow flex items-center gap-1.5"><Icon className="h-3 w-3" />{label}</p>
            <p className="text-sm mt-1 break-words">{value || "—"}</p>
        </div>
    );
}
